import { Capacitor } from '@capacitor/core';
import { useEffect, useRef, useState } from 'react';

/** px of pull needed to trigger a refresh */
const THRESHOLD = 80;
/** max visual travel of the indicator */
const MAX_PULL = 110;
/** dampen finger movement so indicator moves at half speed */
const DAMPEN = 0.5;
const RELOAD_COOLDOWN_MS = 10_000;
const LAST_RELOAD_KEY = 'yubu_pull_refresh_last_reload';

function isInteractiveElement(target) {
  return Boolean(
    target?.closest?.('input, textarea, select, button, a, [role="button"], .modal, .dropdown-menu')
  );
}

function canTriggerReload() {
  const lastReload = Number(sessionStorage.getItem(LAST_RELOAD_KEY) || 0);
  return !lastReload || Date.now() - lastReload > RELOAD_COOLDOWN_MS;
}

/**
 * Adds a pull-down-to-refresh gesture on native (Capacitor) builds.
 * Renders nothing on web.
 */
export default function PullToRefresh() {
  const [pullY, setPullY] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  // Refs hold mutable values used inside event handlers — avoids re-adding listeners
  const startYRef = useRef(null);
  const currentPullRef = useRef(0);
  const pullingRef = useRef(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    function onTouchStart(e) {
      if (refreshing || e.touches.length !== 1 || isInteractiveElement(e.target)) {
        return;
      }

      if (window.scrollY === 0 && canTriggerReload()) {
        startYRef.current = e.touches[0].clientY;
        pullingRef.current = false;
      }
    }

    function onTouchMove(e) {
      if (startYRef.current === null) return;

      const delta = e.touches[0].clientY - startYRef.current;

      if (delta > 0 && window.scrollY === 0) {
        // Prevent the native WebView from scrolling while we handle the gesture
        e.preventDefault();
        pullingRef.current = true;
        const clamped = Math.min(delta * DAMPEN, MAX_PULL);
        currentPullRef.current = clamped;
        setPullY(clamped);
      } else if (delta <= 0 && pullingRef.current) {
        pullingRef.current = false;
        startYRef.current = null;
        currentPullRef.current = 0;
        setPullY(0);
      }
    }

    function onTouchEnd() {
      if (!pullingRef.current) return;

      if (currentPullRef.current >= THRESHOLD && canTriggerReload()) {
        sessionStorage.setItem(LAST_RELOAD_KEY, String(Date.now()));
        setRefreshing(true);
        setPullY(0);
        // Short delay so the spinner is visible before reload
        setTimeout(() => window.location.reload(), 400);
      } else {
        setPullY(0);
      }

      startYRef.current = null;
      currentPullRef.current = 0;
      pullingRef.current = false;
    }

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [refreshing]);

  if (!Capacitor.isNativePlatform()) return null;
  if (!pullY && !refreshing) return null;

  const progress = Math.min(pullY / THRESHOLD, 1);
  const pastThreshold = pullY >= THRESHOLD;

  // Slide down with the finger; snap to a fixed position while refreshing
  const translateY = refreshing ? 52 : pullY - 44;

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        display: 'flex',
        justifyContent: 'center',
        pointerEvents: 'none',
        transform: `translateY(${translateY}px)`,
        transition: refreshing || pullY === 0 ? 'transform 0.25s ease' : 'none',
      }}
    >
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: '50%',
          background: '#ffffff',
          boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: refreshing ? 1 : 0.3 + progress * 0.7,
        }}
      >
        {refreshing ? (
          <div
            className="spinner-border text-primary"
            role="status"
            style={{ width: 20, height: 20, borderWidth: 2 }}
          />
        ) : (
          <i
            className="fas fa-arrow-down"
            style={{
              fontSize: 15,
              color: pastThreshold ? '#0d6efd' : '#6c757d',
              transform: `rotate(${pastThreshold ? 180 : Math.round(progress * 160)}deg)`,
              transition: 'transform 0.15s ease, color 0.15s ease',
            }}
          />
        )}
      </div>
    </div>
  );
}
